import { orderTypeIds } from '../constants/order';
import orderTotals from '../constants/orderTotals';
import { storeIds } from '../constants/store';
import siteSetting from '../constants/siteSetting';
import tipsTypes from '../constants/tipsTypes';
import { getCartSubtotal } from './orders';
import { resolveAmplitudePageName } from './amplitudePageMapping';
import { removeCountryPathFromLocation } from './pathes';

/**
 * @param {number|string|undefined|null} orderTypeId
 * @returns {'Delivery'|'Pickup'|'Shipping'|''}
 */
export const fulfillmentTypeFromOrderTypeId = (orderTypeId) => {
  const id = Number(orderTypeId);
  if (id === orderTypeIds.delivery) return 'Delivery';
  if (id === orderTypeIds.pickup) return 'Pickup';
  if (id === orderTypeIds.shipping) return 'Shipping';
  return '';
};

/**
 * @param {{ orderTypeId?: number|string }} [variables]
 */
export const fulfillmentTypeLabelFromCreateCartVariables = (variables) =>
  fulfillmentTypeFromOrderTypeId(variables?.orderTypeId);

/** Spec spelling for Amplitude commerce events. */
export const AMPLITUDE_PRODUCT_TYPE_CUSTOMIZABLE = 'Customizeable';
export const AMPLITUDE_PRODUCT_TYPE_NON_CUSTOMIZABLE = 'Non-customizable';

/**
 * Top rollup category for analytics (e.g. Packs, Cookies).
 * @param {{ categories?: { title?: string }[] }} product
 */
export const getProductRollupCategory = (product) => {
  if (!product?.categories?.length) {
    return undefined;
  }
  const title = product.categories[0]?.title;
  return title != null && String(title).trim() !== '' ? String(title).trim() : undefined;
};

/**
 * @param {object} product
 * @param {{ isBox?: boolean, isCustom?: boolean }} [flags]
 */
export const getAmplitudeProductType = (product, flags = {}) => {
  const { isBox, isCustom } = flags;
  if (isBox || isCustom) {
    return AMPLITUDE_PRODUCT_TYPE_CUSTOMIZABLE;
  }
  const hasAssortments = Boolean(product?.hasAssortments);
  const hasConfigurableOptions = Boolean(product?.options?.length);
  if (hasAssortments || hasConfigurableOptions) {
    return AMPLITUDE_PRODUCT_TYPE_CUSTOMIZABLE;
  }
  return AMPLITUDE_PRODUCT_TYPE_NON_CUSTOMIZABLE;
};

/**
 * @param {{ productOptions?: unknown[] }} [orderLineItem]
 */
export const countLineItemProductOptions = (orderLineItem) => {
  const opts = orderLineItem?.productOptions;
  const n = Array.isArray(opts) ? opts.length : 0;
  const box = orderLineItem?.boxOptions;
  const nb = Array.isArray(box) ? box.length : 0;
  return n + nb;
};

export const normalizePathnameForAmplitudeCommerce = (pathname) => {
  if (!pathname || typeof pathname !== 'string') {
    return '';
  }
  const noQuery = pathname.split(/[?#]/)[0];
  return removeCountryPathFromLocation(noQuery);
};

const isPdpPath = (path) =>
  /^\/products\//.test(path) || /^\/single-product(\/|$)/.test(path);

/**
 * Page Name for "Add to Order" (web spec: Home, Menu, PDP, Gift Options).
 * @param {string} [pathname] - router location.pathname
 */
export const resolveAddToOrderPageName = (pathname) => {
  const path = normalizePathnameForAmplitudeCommerce(pathname);
  const mapped = resolveAmplitudePageName(path);
  if (mapped === 'Home') {
    return 'Home';
  }
  if (mapped === 'Gift Options') {
    return 'Gift Options';
  }
  if (isPdpPath(path)) {
    return 'PDP';
  }
  if (mapped === 'Menu') {
    return 'Menu';
  }
  return mapped || 'Menu';
};

/**
 * Pick the line item most likely added in this mutation (last line matching product id).
 * @param {{ items?: object[] }} orderLike
 * @param {string|number} productId
 */
export const findLatestOrderLineForProduct = (orderLike, productId) => {
  const items = orderLike?.items;
  if (!Array.isArray(items) || productId == null) {
    return undefined;
  }
  const idStr = String(productId);
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const line = items[i];
    if (line?.product?.id != null && String(line.product.id) === idStr) {
      return line;
    }
  }
  return undefined;
};

/**
 * Prefer the cart line created by this add (new order line id vs previous snapshot).
 * @param {{ items?: object[] }} previousOrder
 * @param {{ items?: object[] }} nextOrder
 * @param {string|number} [productId]
 */
export const findAddedOrderLine = (previousOrder, nextOrder, productId) => {
  const prevItems = previousOrder?.items;
  const nextItems = nextOrder?.items;
  if (!Array.isArray(nextItems) || nextItems.length === 0) {
    return undefined;
  }
  const prevIds = new Set(
    (Array.isArray(prevItems) ? prevItems : [])
      .filter((li) => li?.id != null)
      .map((li) => String(li.id)),
  );
  const newLines = nextItems.filter((li) => li?.id != null && !prevIds.has(String(li.id)));
  const pid = productId != null ? String(productId) : null;
  if (newLines.length > 0) {
    if (pid) {
      const matching = newLines.filter(
        (li) => li?.product?.id != null && String(li.product.id) === pid,
      );
      if (matching.length === 1) {
        return matching[0];
      }
      if (matching.length > 1) {
        return matching[matching.length - 1];
      }
    }
    return newLines[newLines.length - 1];
  }
  return pid ? findLatestOrderLineForProduct(nextOrder, productId) : undefined;
};

/**
 * Single-line address for Amplitude.
 * @param {object} [addr]
 */
export const formatAddressLineForAmplitude = (addr) => {
  if (!addr || typeof addr !== 'object') {
    return '';
  }
  const line1 = [addr.address1, addr.address2].filter(Boolean).join(', ');
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
  const country =
    addr.country != null && String(addr.country).trim() !== ''
      ? String(addr.country).trim()
      : '';
  const postcode =
    addr.postcode != null && String(addr.postcode).trim() !== ''
      ? String(addr.postcode).trim()
      : '';
  const segments = [line1, cityState, country, postcode].filter(
    (s) => s != null && String(s).trim() !== '',
  );
  return segments.map((s) => String(s).trim()).join(', ');
};

/**
 * Shared checkout address / store / fulfillment (Place Order & Complete Order).
 * @param {object} [order]
 * @param {object} [reduxState]
 * @returns {object|null}
 */
export const resolveCheckoutFlowBaseProperties = (order, reduxState) => {
  if (!order) {
    return null;
  }
  const selectedStore = reduxState?.user?.selectedStore;
  const userAddress = reduxState?.user?.userAddress;
  const sid = order.store?.id;
  const storeIdStr = sid != null ? String(sid) : '';

  const fulfillmentType = fulfillmentTypeLabelFromCreateCartVariables({
    orderTypeId: order.orderTypeId,
  });

  let addressLine = formatAddressLineForAmplitude(order.deliveryAddress);
  const shippingAddr = order.shippingAddress;
  const mergedShipping =
    shippingAddr && order.shippingAddress2
      ? { ...shippingAddr, address2: order.shippingAddress2 }
      : shippingAddr;
  if (!addressLine) {
    addressLine = formatAddressLineForAmplitude(mergedShipping);
  }
  if (!addressLine && order.store) {
    addressLine = formatAddressLineForAmplitude({
      address1: order.store.mailingAddress ?? order.store.address,
      address2: order.store.address2,
      city: order.store.city,
      state: order.store.state,
      postcode: order.store.zip ?? order.store.postcode,
      country: order.store.country,
    });
  }
  if (!addressLine && order.store?.address && typeof order.store.address === 'string') {
    const single = String(order.store.address).trim();
    if (single) {
      addressLine = single;
    }
  }
  if (!addressLine && userAddress) {
    addressLine = formatAddressLineForAmplitude(userAddress);
  }

  const isShippingOrder =
    Number(order.orderTypeId) === orderTypeIds.shipping ||
    String(sid) === String(storeIds.shipping);

  let storeName = '';
  if (
    selectedStore != null &&
    String(selectedStore.id) === String(sid) &&
    selectedStore.name != null &&
    String(selectedStore.name).trim() !== ''
  ) {
    storeName = String(selectedStore.name).trim();
  } else if (!isShippingOrder && order.store?.name) {
    storeName = String(order.store.name).trim();
  }
  if (isShippingOrder) {
    storeName = 'Gifts';
  }

  return {
    'Fulfillment Type': fulfillmentType,
    Address: addressLine,
    'Store Name': storeName,
    'Store ID': storeIdStr,
  };
};

export const sumOrderLineItemQuantities = (order) => {
  const items = order?.items;
  if (!Array.isArray(items)) {
    return 0;
  }
  return items.reduce((sum, line) => {
    const q = Number(line?.quantity);
    return sum + (Number.isFinite(q) && q > 0 ? q : 0);
  }, 0);
};

/**
 * Subtotal from line items (aligns with cart subtotal math).
 * @param {{ items?: object[] }} [order]
 * @param {string} [currencySymbol]
 */
export const formatOrderSubtotalFromLineItems = (order, currencySymbol = '$') => {
  const sym =
    currencySymbol != null && String(currencySymbol).trim() !== ''
      ? String(currencySymbol).trim()
      : '$';
  const items = order?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return `${sym}0.00`;
  }
  const subtotal = getCartSubtotal(items);
  const n = Number(subtotal);
  return `${sym}${Number.isFinite(n) ? n.toFixed(2) : '0.00'}`;
};

/**
 * @param {Date|number|string} [date]
 */
export const formatOrderCreatedDateForAmplitude = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const timeZone = 'America/New_York';
  const options = {
    timeZone,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };
  let formatted = new Intl.DateTimeFormat('en-US', options).format(d);
  const subMs = ((d.getTime() % 1000) + 1000) % 1000;
  formatted = formatted.replace(
    /(\d{1,2}:\d{2}:\d{2}) (AM|PM)/i,
    (_, clock, ap) => `${clock}.${String(subMs).padStart(3, '0')} ${ap}`,
  );
  formatted = formatted.replace(/(\d{4}),/, '$1');
  return formatted;
};

/**
 * Build "Add First Item to Cart" event properties from createCart variables + Redux.
 * @param {object} variables
 * @param {object} [reduxState]
 * @param {Date} [eventTime]
 */
export const resolveCreateCartEventProperties = (variables, reduxState, eventTime = new Date()) => {
  const selectedStore = reduxState?.user?.selectedStore;
  const userAddress = reduxState?.user?.userAddress;
  const sid = variables?.storeId;
  const storeId = sid != null ? String(sid) : '';
  const fulfillmentType = fulfillmentTypeLabelFromCreateCartVariables(variables);

  let addressLine = formatAddressLineForAmplitude(variables?.address);

  if (!addressLine && selectedStore != null && String(selectedStore.id) === String(sid)) {
    addressLine = formatAddressLineForAmplitude({
      address1: selectedStore.mailingAddress ?? selectedStore.address,
      address2: selectedStore.address2,
      city: selectedStore.city,
      state: selectedStore.state,
      postcode: selectedStore.zip ?? selectedStore.postcode,
    });
  }

  if (!addressLine && userAddress) {
    addressLine = formatAddressLineForAmplitude(userAddress);
  }

  const isShippingCart =
    Number(variables?.orderTypeId) === orderTypeIds.shipping ||
    String(sid) === String(storeIds.shipping);

  let storeName = '';
  if (isShippingCart) {
    storeName = 'Gifts';
  }
  if (
    selectedStore != null &&
    String(selectedStore.id) === String(sid) &&
    selectedStore.name != null &&
    String(selectedStore.name).trim() !== ''
  ) {
    storeName = String(selectedStore.name).trim();
  }

  return {
    'Order Created Date': formatOrderCreatedDateForAmplitude(eventTime),
    'Fulfillment Type': fulfillmentType,
    Address: addressLine,
    'Store Name': storeName,
    'Store ID': storeId,
  };
};

/**
 * Same keys as {@link resolveCreateCartEventProperties}, from the order after the first
 * cart line exists (createCart alone may not have added a line yet).
 */
export const resolveAddFirstItemToCartEventPropertiesFromOrder = (
  order,
  reduxState,
  eventTime = new Date(),
) => {
  const selectedStore = reduxState?.user?.selectedStore;
  const userAddress = reduxState?.user?.userAddress;
  const sid = order?.store?.id;
  const storeId = sid != null ? String(sid) : '';
  const fulfillmentType = fulfillmentTypeLabelFromCreateCartVariables({
    orderTypeId: order?.orderTypeId,
  });

  const shipOrDelivery = order?.shippingAddress || order?.deliveryAddress;
  let addressLine = formatAddressLineForAmplitude(shipOrDelivery);

  if (!addressLine && selectedStore != null && String(selectedStore.id) === String(sid)) {
    addressLine = formatAddressLineForAmplitude({
      address1: selectedStore.mailingAddress ?? selectedStore.address,
      address2: selectedStore.address2,
      city: selectedStore.city,
      state: selectedStore.state,
      postcode: selectedStore.zip ?? selectedStore.postcode,
    });
  }

  if (!addressLine && userAddress) {
    addressLine = formatAddressLineForAmplitude(userAddress);
  }

  const isShippingCart =
    Number(order?.orderTypeId) === orderTypeIds.shipping ||
    String(sid) === String(storeIds.shipping);

  let storeName = '';
  if (isShippingCart) {
    storeName = 'Gifts';
  }
  if (
    selectedStore != null &&
    String(selectedStore.id) === String(sid) &&
    selectedStore.name != null &&
    String(selectedStore.name).trim() !== ''
  ) {
    storeName = String(selectedStore.name).trim();
  }
  if (!storeName && order?.store?.name != null && String(order.store.name).trim() !== '') {
    storeName = String(order.store.name).trim();
  }

  const orderCreated =
    order?.createdAt != null ? new Date(order.createdAt) : eventTime;

  return {
    'Order Created Date': formatOrderCreatedDateForAmplitude(orderCreated),
    'Fulfillment Type': fulfillmentType,
    Address: addressLine,
    'Store Name': storeName,
    'Store ID': storeId,
  };
};

const normalizeTotalTitle = (title) =>
  String(title || '')
    .toLowerCase()
    .replace(/\s/g, '');

/**
 * Sum delivery + extended delivery line amounts from order totals.
 * @param {{ totals?: { title?: string, value?: number|string }[] }} [order]
 * @returns {number}
 */
export const sumDeliveryFeeFromOrderTotals = (order) => {
  const rows = order?.totals;
  if (!Array.isArray(rows)) {
    return 0;
  }
  const d1 = orderTotals.delivery.title.toLowerCase();
  const d2 = orderTotals.extendedDelivery.title.toLowerCase();
  let sum = 0;
  rows.forEach((row) => {
    const t = String(row.title || '').toLowerCase();
    if (t !== d1 && t !== d2) {
      return;
    }
    const v = row.value;
    if (v === 'Free') {
      return;
    }
    const n = Number(v);
    if (Number.isFinite(n)) {
      sum += n;
    }
  });
  return sum;
};

const getSubtotalAndTaxFromOrder = (order) => {
  let subtotal = 0;
  let tax = 0;
  (order?.totals || []).forEach((total) => {
    const title = normalizeTotalTitle(total.title);
    if (title === 'subtotal') {
      subtotal = Number(total.value) || 0;
    } else if (title === 'tax') {
      tax = Number(total.value) || 0;
    }
  });
  return { subtotal, tax };
};

const calculatePresetTipAmount = (order, tipValue, tipsType) => {
  if (tipsType === tipsTypes.TYPE_PERCENT) {
    const { subtotal, tax } = getSubtotalAndTaxFromOrder(order);
    return ((subtotal + tax) / 100) * Number(tipValue);
  }
  if (tipsType === tipsTypes.TYPE_AMOUNT) {
    return Number(tipValue) || 0;
  }
  return Number(tipValue) || 0;
};

/**
 * Tip option label for Complete Order, e.g. 18%, 20%, Custom.
 * @param {object} [order]
 * @param {{ tip?: number }} [checkout]
 * @param {object} [tipsSettings]
 * @param {string} [currencySymbol]
 */
export const resolveAmplitudeTipSelectionLabel = (
  order,
  checkout,
  tipsSettings,
  currencySymbol = '$',
) => {
  const ts = tipsSettings && typeof tipsSettings === 'object' ? tipsSettings : {};
  const type = ts[siteSetting.TIPS_TYPE] ?? tipsTypes.TYPE_PERCENT;
  const rawValues = ts[siteSetting.TIPS_VALUES];
  const values = Array.isArray(rawValues) ? rawValues : [18, 20, 25];
  const tipPaid = Number(checkout?.tip);
  const tipPaidNum = Number.isFinite(tipPaid) ? tipPaid : 0;

  if (type === tipsTypes.TYPE_PERCENT) {
    for (let i = 0; i < values.length; i += 1) {
      const val = values[i];
      const amt = calculatePresetTipAmount(order, val, type);
      if (tipPaidNum.toFixed(2) === parseFloat(amt).toFixed(2)) {
        return `${val}%`;
      }
    }
    return 'Custom';
  }

  if (type === tipsTypes.TYPE_AMOUNT) {
    for (let i = 0; i < values.length; i += 1) {
      const val = values[i];
      const amt = calculatePresetTipAmount(order, val, type);
      if (tipPaidNum.toFixed(2) === parseFloat(amt).toFixed(2)) {
        return `${currencySymbol}${val}`;
      }
    }
    return 'Custom';
  }

  return 'Custom';
};

/**
 * Place Order ? Fulfillment Type, Address, Store Name, Store ID.
 * @param {object} [order]
 * @param {object} [reduxState]
 * @returns {object|null}
 */
export const resolvePlaceOrderEventProperties = (order, reduxState) => {
  if (!order?.code) {
    return null;
  }
  const base = resolveCheckoutFlowBaseProperties(order, reduxState);
  if (!base) {
    return null;
  }

  let orderIdStr = '';
  if (order?.id != null && String(order.id).trim() !== '') {
    orderIdStr = String(order.id);
  } else if (order?.code != null) {
    orderIdStr = String(order.code);
  }

  return {
    ...base,
    'Order ID': orderIdStr,
  };
};

const resolveOrderCreatedDateStringForCompleteOrder = (order, meta) => {
  if (meta?.eventTime !== undefined && meta?.eventTime !== null) {
    return formatOrderCreatedDateForAmplitude(meta.eventTime);
  }
  if (order?.createdAt) {
    const d = new Date(order.createdAt);
    if (!Number.isNaN(d.getTime())) {
      return formatOrderCreatedDateForAmplitude(d);
    }
  }
  return formatOrderCreatedDateForAmplitude(new Date());
};

/**
 * Complete Order ? full funnel payload after successful place order.
 * @param {object} [order]
 * @param {object} [reduxState]
 * @param {object} [meta]
 * @param {object} [meta.checkout] ? Redux checkout (tip)
 * @param {number} [meta.checkoutTotal] ? order total (e.g. previousTotal)
 * @param {object} [meta.tipsSettings] ? `state.tips.tipsSettings`
 * @param {boolean} [meta.dropoffLocationSet]
 * @param {Date} [meta.eventTime] ? tests / override created time
 * @returns {object|null}
 */
export const resolveCompleteOrderEventProperties = (order, reduxState, meta = {}) => {
  if (!order?.code) {
    return null;
  }
  const base = resolveCheckoutFlowBaseProperties(order, reduxState);
  if (!base) {
    return null;
  }
  const { checkout, checkoutTotal, tipsSettings, dropoffLocationSet = false } = meta;
  const currencySymbol =
    order?.store?.currency?.symbol ??
    reduxState?.currency?.currency?.symbol ??
    '$';

  const deliveryFeeNum = sumDeliveryFeeFromOrderTotals(order);
  const deliveryFeeDisplay = `${currencySymbol}${deliveryFeeNum.toFixed(2)}`;

  const tipLabel = resolveAmplitudeTipSelectionLabel(
    order,
    checkout,
    tipsSettings,
    currencySymbol,
  );

  const subtotalDisplay = formatOrderSubtotalFromLineItems(order, currencySymbol);
  const totalNum = Number(checkoutTotal);
  const totalDisplay = Number.isFinite(totalNum)
    ? `${currencySymbol}${totalNum.toFixed(2)}`
    : `${currencySymbol}0.00`;

  let orderIdStr = '';
  if (order?.id != null && String(order.id).trim() !== '') {
    orderIdStr = String(order.id);
  } else if (order?.code != null) {
    orderIdStr = String(order.code);
  }

  const orderCreatedDate = resolveOrderCreatedDateStringForCompleteOrder(order, meta);

  return {
    'Order Created Date': orderCreatedDate,
    'Fulfillment Type': base['Fulfillment Type'],
    Address: base.Address,
    'Dropoff Location Set': Boolean(dropoffLocationSet),
    'Store Name': base['Store Name'],
    'Store ID': base['Store ID'],
    'Number of Items': sumOrderLineItemQuantities(order),
    Subtotal: subtotalDisplay,
    'Delivery Fee': deliveryFeeDisplay,
    'Tip %': tipLabel,
    Total: totalDisplay,
    'Order ID': orderIdStr,
  };
};

/**
 * View Checkout / View Order — shared flow fields (shipping-first address line).
 * Separate from {@link resolveCheckoutFlowBaseProperties} used for Place/Complete Order (#18389).
 * @param {object} order — GraphQL order
 * @param {object} [reduxState] — store.getState()
 */
export const resolveViewCheckoutOrderBaseProperties = (order, reduxState) => {
  const fulfillment = fulfillmentTypeFromOrderTypeId(order?.orderTypeId);
  const selectedStore = reduxState?.user?.selectedStore;
  const sid = order?.store?.id;
  const storeIdStr = sid != null ? String(sid) : '';

  const shippingAddr = order?.shippingAddress;
  const mergedShipping =
    shippingAddr && order?.shippingAddress2
      ? { ...shippingAddr, address2: order.shippingAddress2 }
      : shippingAddr;

  let addressLine = formatAddressLineForAmplitude(mergedShipping);

  if (!addressLine && order?.store) {
    addressLine = formatAddressLineForAmplitude({
      address1: order.store.address,
      city: order.store.city,
      state: order.store.state,
      postcode: order.store.zip ?? order.store.postcode,
      country: order.store.country,
    });
  }

  if (!addressLine && reduxState?.user?.userAddress) {
    addressLine = formatAddressLineForAmplitude(reduxState.user.userAddress);
  }

  const isShippingOrder =
    Number(order?.orderTypeId) === orderTypeIds.shipping ||
    String(sid) === String(storeIds.shipping);

  let storeName = '';
  if (order?.store?.name != null && String(order.store.name).trim() !== '') {
    storeName = String(order.store.name).trim();
  }
  if (
    !storeName &&
    selectedStore != null &&
    String(selectedStore.id) === String(sid) &&
    selectedStore.name != null &&
    String(selectedStore.name).trim() !== ''
  ) {
    storeName = String(selectedStore.name).trim();
  }
  if (isShippingOrder) {
    storeName = 'Gifts';
  }

  return {
    'Fulfillment Type': fulfillment,
    Address: addressLine,
    'Store Name': storeName,
    'Store ID': storeIdStr,
  };
};

/** Parse money from API (number, numeric string, optional thousands commas). #18389 */
const parseAmountForAmplitudeViewOrder = (raw) => {
  if (raw == null || raw === '') {
    return NaN;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  const s = String(raw).trim().replace(/,/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Find subtotal in `order.totals[]` — exact title match first, then any title containing "subtotal"
 * (#18389 view-order payloads).
 */
const subtotalFromTotalsRowsViewOrder = (totals) => {
  if (!Array.isArray(totals)) {
    return NaN;
  }
  const titleLower = (t) => String(t?.title ?? '').toLowerCase().trim();
  let row = totals.find((t) => titleLower(t) === 'subtotal');
  if (!row) {
    row = totals.find((t) => {
      const tl = titleLower(t);
      return tl !== '' && tl.includes('subtotal');
    });
  }
  if (row == null) {
    return NaN;
  }
  return parseAmountForAmplitudeViewOrder(row.value);
};

/**
 * Per-unit line revenue for View Order subtotal (#18389).
 * @param {object} line — GraphQL order line item
 */
const orderLineUnitPriceForAmplitudeViewOrder = (line) => {
  let unit = parseAmountForAmplitudeViewOrder(line?.price);
  if (!Number.isFinite(unit)) {
    unit = parseAmountForAmplitudeViewOrder(line?.product?.price);
  }
  if (!Number.isFinite(unit)) {
    return NaN;
  }
  if (!Array.isArray(line?.productOptions)) {
    return unit;
  }
  line.productOptions.forEach((opt) => {
    const oq = Number(opt?.quantity);
    const optQty = Number.isFinite(oq) && oq > 0 ? oq : 0;
    if (!optQty) {
      return;
    }
    if (opt?.productOption != null && Number(opt.productOption.price) > 0) {
      const p = parseAmountForAmplitudeViewOrder(opt.productOption.price);
      if (Number.isFinite(p)) {
        unit += p * optQty;
      }
    } else {
      const op = parseAmountForAmplitudeViewOrder(opt?.optionPrice);
      if (Number.isFinite(op) && op > 0) {
        unit += op * optQty;
      }
    }
  });
  return unit;
};

/** Sum line revenue when order subtotal / totals are missing (#18389). */
const subtotalFromOrderLineItemsViewOrder = (order) => {
  const items = order?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return NaN;
  }
  let sum = 0;
  let counted = false;
  items.forEach((line) => {
    const q = Number(line?.quantity);
    const qty = Number.isFinite(q) && q > 0 ? q : 0;
    if (!qty) {
      return;
    }
    const unit = orderLineUnitPriceForAmplitudeViewOrder(line);
    if (Number.isFinite(unit)) {
      sum += unit * qty;
      counted = true;
    }
  });
  return counted ? sum : NaN;
};

/**
 * Subtotal string e.g. `$20.19` for View Order (#18389).
 * @param {object} order
 * @param {string} [currencySymbol]
 */
export const formatOrderSubtotalForAmplitude = (order, currencySymbol = '$') => {
  const sym =
    currencySymbol != null && String(currencySymbol).trim() !== ''
      ? String(currencySymbol).trim()
      : '$';

  let amount = parseAmountForAmplitudeViewOrder(order?.subtotal);
  if (!Number.isFinite(amount)) {
    amount = subtotalFromTotalsRowsViewOrder(order?.totals);
  }
  if (!Number.isFinite(amount)) {
    amount = subtotalFromOrderLineItemsViewOrder(order);
  }

  if (!Number.isFinite(amount)) {
    return `${sym}0.00`;
  }
  return `${sym}${amount.toFixed(2)}`;
};
